import { useState, useEffect } from 'react';
import { api, Organization } from '../api';
import { getErrorMessage } from '../../../shared/src';

interface SsoConfigPanelProps {
  org: Organization;
  onUpdate: (org: Organization) => void;
}

export function SsoConfigPanel({ org, onUpdate }: SsoConfigPanelProps) {
  const [providerType, setProviderType] = useState<'saml' | 'oidc'>('saml');
  const [config, setConfig] = useState({
    providerName: '',
    isEnabled: false,
    isPrimary: true,
    samlEntityId: '',
    samlSsoUrl: '',
    samlSloUrl: '',
    samlCertificate: '',
    samlNameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
    oidcIssuerUrl: '',
    oidcClientId: '',
    oidcClientSecret: '',
    oidcScopes: 'openid profile email',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (org.sso_configs && org.sso_configs.length > 0) {
      const existing = org.sso_configs[0];
      setProviderType(existing.provider_type);
      setConfig((prev) => ({
        ...prev,
        providerName: existing.provider_name || '',
        isEnabled: existing.is_enabled,
        isPrimary: existing.is_primary,
        samlEntityId: existing.saml_entity_id || '',
        samlSsoUrl: existing.saml_sso_url || '',
        samlSloUrl: existing.saml_slo_url || '',
        samlNameIdFormat: existing.saml_name_id_format || prev.samlNameIdFormat,
        oidcIssuerUrl: existing.oidc_issuer_url || '',
        oidcClientId: existing.oidc_client_id || '',
        oidcScopes: existing.oidc_scopes || prev.oidcScopes,
      }));
    }
  }, [org.sso_configs]);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.configureSso(org.id, { providerType, ...config });
      setMessage('SSO configuration saved successfully');
      const updated = await api.getOrganization(org.id);
      onUpdate(updated);
    } catch (err: unknown) {
      setMessage(`Error: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="sso-form">
        <div className="form-row">
          <div className="form-group">
            <label>Provider Type</label>
            <select value={providerType} onChange={(e) => setProviderType(e.target.value as 'saml' | 'oidc')}>
              <option value="saml">SAML 2.0</option>
              <option value="oidc">OpenID Connect</option>
            </select>
          </div>
          <div className="form-group">
            <label>Provider Name</label>
            <input
              type="text"
              value={config.providerName}
              onChange={(e) => setConfig({ ...config, providerName: e.target.value })}
              placeholder="e.g., Okta, Azure AD"
            />
          </div>
        </div>

        <div className="form-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={config.isEnabled}
              onChange={(e) => setConfig({ ...config, isEnabled: e.target.checked })}
            />
            Enabled
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={config.isPrimary}
              onChange={(e) => setConfig({ ...config, isPrimary: e.target.checked })}
            />
            Primary Provider
          </label>
        </div>

        {providerType === 'saml' && (
          <>
            <h3>SAML Configuration</h3>
            <div className="form-group">
              <label>IdP Entity ID (Issuer)</label>
              <input
                type="text"
                value={config.samlEntityId}
                onChange={(e) => setConfig({ ...config, samlEntityId: e.target.value })}
                placeholder="https://idp.example.com/saml"
              />
            </div>
            <div className="form-group">
              <label>SSO URL (Login URL)</label>
              <input
                type="url"
                value={config.samlSsoUrl}
                onChange={(e) => setConfig({ ...config, samlSsoUrl: e.target.value })}
                placeholder="https://idp.example.com/saml/sso"
              />
            </div>
            <div className="form-group">
              <label>SLO URL (Logout URL) - Optional</label>
              <input
                type="url"
                value={config.samlSloUrl}
                onChange={(e) => setConfig({ ...config, samlSloUrl: e.target.value })}
                placeholder="https://idp.example.com/saml/slo"
              />
            </div>
            <div className="form-group">
              <label>IdP Certificate (X.509 PEM format)</label>
              <textarea
                value={config.samlCertificate}
                onChange={(e) => setConfig({ ...config, samlCertificate: e.target.value })}
                placeholder="-----BEGIN CERTIFICATE-----..."
                rows={6}
              />
            </div>

            <div className="info-box">
              <h4>SP Metadata for IdP Configuration</h4>
              <div className="info-item">
                <label>Entity ID:</label>
                <code>https://api.fakto.ai/saml/metadata</code>
              </div>
              <div className="info-item">
                <label>ACS URL:</label>
                <code>https://api.fakto.ai/auth/saml/callback</code>
              </div>
            </div>
          </>
        )}

        {providerType === 'oidc' && (
          <>
            <h3>OIDC Configuration</h3>
            <div className="form-group">
              <label>Issuer URL</label>
              <input
                type="url"
                value={config.oidcIssuerUrl}
                onChange={(e) => setConfig({ ...config, oidcIssuerUrl: e.target.value })}
                placeholder="https://idp.example.com"
              />
            </div>
            <div className="form-group">
              <label>Client ID</label>
              <input
                type="text"
                value={config.oidcClientId}
                onChange={(e) => setConfig({ ...config, oidcClientId: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Client Secret</label>
              <input
                type="password"
                value={config.oidcClientSecret}
                onChange={(e) => setConfig({ ...config, oidcClientSecret: e.target.value })}
                placeholder="Enter to update"
              />
            </div>

            <div className="info-box">
              <h4>Redirect URI</h4>
              <code>https://api.fakto.ai/auth/oidc/callback</code>
            </div>
          </>
        )}

        {message && <div className={message.startsWith('Error') ? 'error-message' : 'success-message'}>{message}</div>}
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}
